'use client';

import { FC, useState, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import Link from 'next/link';
import { registerContact } from '@/actions/hubspot/registerContact';
import cookies from 'js-cookie';
import { useIsClient } from '@/hooks';

// HubSpot form configuration
const DEFAULT_HUBSPOT_PORTAL_ID = '7119515';
const DEFAULT_HUBSPOT_FORM_ID = 'c54880e9-1a1d-4609-a81e-a1133f6b5d1c';

// Field label to HubSpot internal name mapping.
// Keys are lowercased + trimmed labels (asterisks/colons stripped); values are
// the exact internal property names that HubSpot expects.
const FIELD_MAPPINGS: Record<string, string> = {
  // First name
  name: 'firstname',
  'first name': 'firstname',
  firstname: 'firstname',
  'full name': 'firstname',
  'your name': 'firstname',
  // Last name
  'last name': 'lastname',
  lastname: 'lastname',
  surname: 'lastname',
  // Email
  email: 'email',
  'email address': 'email',
  'your email': 'email',
  'work email': 'email',
  // Company
  company: 'company',
  'company name': 'company',
  'your company': 'company',
  organization: 'company',
  // Job title
  'job title': 'jobtitle',
  jobtitle: 'jobtitle',
  title: 'jobtitle',
  role: 'jobtitle',
  position: 'jobtitle',
  // Phone
  phone: 'phone',
  'phone number': 'phone',
  mobile: 'phone',
};

/**
 * Props for `CtaWithSideForm`.
 */
export type CtaWithSideFormProps =
  SliceComponentProps<Content.CtaWithSideFormSlice>;

/**
 * Component for "CtaWithSideForm" Slices.
 */
const CtaWithSideForm: FC<CtaWithSideFormProps> = ({ slice }) => {
  const {
    hero_logo,
    headline,
    subheading,
    supporting_text,
    form_fields,
    form_headline,
    form_description,
    submit_button_label,
    form_id,
  } = slice.primary;

  // Use form_id from Prismic if provided, otherwise use default
  const hubspotFormId = form_id || DEFAULT_HUBSPOT_FORM_ID;

  const logoRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Track if animations have been initialized to prevent flicker
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const elementsToAnimate = [
      { ref: logoRef, delay: '0ms' },
      { ref: contentRef, delay: '150ms' },
      { ref: formRef, delay: '300ms' },
    ];

    elementsToAnimate.forEach(({ ref, delay }) => {
      if (ref.current) {
        ref.current.style.transitionDelay = delay;
        observer.observe(ref.current);
      }
    });

    return () => {
      elementsToAnimate.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [isClient]);

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleInputChange = (fieldLabel: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [fieldLabel]: value,
    }));
    // Clear field error when user types
    if (fieldErrors[fieldLabel]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldLabel];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);
    setFieldErrors({});

    // Validate required fields
    const newErrors: Record<string, string> = {};
    form_fields?.forEach((field) => {
      if (field.is_required && !formData[field.field_label || '']?.trim()) {
        newErrors[field.field_label || ''] = 'This field is required';
      }
      // Validate email format
      if (
        field.field_type === 'email' &&
        formData[field.field_label || '']?.trim()
      ) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[field.field_label || ''])) {
          newErrors[field.field_label || ''] = 'Please enter a valid email';
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      // Convert form field labels to HubSpot field names
      const hubspotProperties: Record<string, string> = {};

      Object.entries(formData).forEach(([label, value]) => {
        const normalizedLabel = label
          .toLowerCase()
          .replace(/[*:]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        const fieldName =
          FIELD_MAPPINGS[normalizedLabel] ||
          normalizedLabel.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        hubspotProperties[fieldName] = value;
      });

      const { postSubmitAction } = await registerContact({
        properties: hubspotProperties,
        pageUri: typeof window !== 'undefined' ? window.location.href : '',
        pageName: typeof document !== 'undefined' ? document.title : '',
        hubspotPortalIdForm: DEFAULT_HUBSPOT_PORTAL_ID,
        hubspotGuidForm: hubspotFormId,
        hubspotutkCookie: cookies.get('hubspotutk'),
      });

      if (postSubmitAction?.type === 'redirect' && postSubmitAction.value) {
        window.location.href = postSubmitAction.value;
        return;
      }

      // Build redirect URL with UTM parameters
      const currentUrl = new URL(window.location.href);
      const redirectParams = new URLSearchParams();

      // Preserve existing UTM parameters
      const utmParams = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
      ];
      utmParams.forEach((param) => {
        const value = currentUrl.searchParams.get(param);
        if (value) {
          redirectParams.set(param, value);
        }
      });

      // Add download identification
      let headlineText = '';
      if (headline && headline.length > 0) {
        const firstNode = headline[0];
        if (firstNode && 'text' in firstNode) {
          headlineText = (firstNode as { text: string }).text;
        }
      }
      const downloadName =
        form_headline || headlineText || document.title || 'ebook-download';
      redirectParams.set(
        'download',
        downloadName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, ''),
      );

      // Add source page
      redirectParams.set('source_page', currentUrl.pathname);

      // Redirect to thank you page
      const thankYouUrl = `/thank-you-for-your-download?${redirectParams.toString()}`;
      window.location.href = thankYouUrl;
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage('Something went wrong. Please try again.');
      setIsSubmitSuccessful(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFormField = (
    field: Content.CtaWithSideFormSliceCtaWithFormEbookPrimaryFormFieldsItem,
    index: number,
  ) => {
    const fieldLabel = field.field_label || '';
    const isRequired = field.is_required;
    const fieldType = field.field_type || 'text';
    const fieldId = fieldLabel.toLowerCase().replace(/\s+/g, '_');

    return (
      <div key={index} className="flex flex-col gap-2 w-full">
        <label
          htmlFor={fieldId}
          className="text-base text-white font-normal leading-[1.5]"
        >
          {fieldLabel}
          {isRequired && ' *'}
        </label>
        {fieldType === 'textarea' ? (
          <textarea
            id={fieldId}
            value={formData[fieldLabel] || ''}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value)}
            placeholder="Type here"
            className="w-full h-[100px] px-3 py-2 bg-[rgba(14,17,16,0.4)] rounded-lg text-white placeholder:text-white/30 text-base leading-[1.5] resize-none focus:outline-none focus:ring-1 focus:ring-[#05f273]/50"
          />
        ) : (
          <input
            id={fieldId}
            type={fieldType === 'email' ? 'email' : 'text'}
            value={formData[fieldLabel] || ''}
            onChange={(e) => handleInputChange(fieldLabel, e.target.value)}
            placeholder="Type here"
            className="w-full h-12 px-3 py-2 bg-[rgba(14,17,16,0.4)] rounded-lg text-white placeholder:text-white/30 text-base leading-[1.5] focus:outline-none focus:ring-1 focus:ring-[#05f273]/50"
          />
        )}
        {fieldErrors[fieldLabel] && (
          <span className="text-red-400 text-sm">
            {fieldErrors[fieldLabel]}
          </span>
        )}
      </div>
    );
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative bg-[#0E1010] py-12 md:py-20 px-4 md:px-8 lg:px-[150px]"
    >
      <div className="max-w-[1140px] mx-auto">
        {/* Logo - positioned at the top, links to homepage */}
        {isFilled.image(hero_logo) && (
          <div
            ref={logoRef}
            className="mb-10 md:mb-16 opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            <Link href="/" aria-label="Devsu home page">
              <PrismicNextImage
                field={hero_logo}
                className="h-6 md:h-8 w-auto object-contain"
              />
            </Link>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-[60px] items-center justify-center">
          {/* Left Column - Content */}
          <div
            ref={contentRef}
            className="flex flex-col gap-4 md:gap-6 text-white w-full lg:w-[520px] opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {/* Headline */}
            {isFilled.richText(headline) && (
              <div className="text-[32px] md:text-[52px] font-extrabold leading-[1.2]">
                <PrismicRichText
                  field={headline}
                  components={{
                    heading2: ({ children }) => <h2>{children}</h2>,
                  }}
                />
              </div>
            )}

            {/* Subheading */}
            {isFilled.richText(subheading) && (
              <div className="text-xl md:text-[24px] font-medium leading-[1.4]">
                <PrismicRichText field={subheading} />
              </div>
            )}

            {/* Supporting Text */}
            {isFilled.richText(supporting_text) && (
              <div className="text-lg md:text-xl font-medium leading-[1.4] text-white/90">
                <PrismicRichText field={supporting_text} />
              </div>
            )}
          </div>

          {/* Right Column - Form Card */}
          <div
            ref={formRef}
            className="w-full lg:w-[460px] bg-[rgba(31,34,40,0.8)] backdrop-blur-[47.5px] rounded-2xl p-6 shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {isSubmitSuccessful ? (
              /* Success State */
              <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
                <div className="w-16 h-16 rounded-full bg-[#05f273]/20 flex items-center justify-center">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 12L10 17L19 8"
                      stroke="#05f273"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3 className="text-2xl font-medium text-white">Thank you!</h3>
                <p className="text-white/70">
                  We&apos;ll send your copy right away.
                </p>
              </div>
            ) : (
              /* Form */
              <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Form Headline */}
                {form_headline && (
                  <h3 className="text-[24px] md:text-[32px] font-medium text-white leading-[1.2]">
                    {form_headline}
                  </h3>
                )}

                {/* Form Description */}
                {isFilled.richText(form_description) && (
                  <div className="text-lg text-white/90 leading-[1.5]">
                    <PrismicRichText field={form_description} />
                  </div>
                )}

                {/* Form Fields */}
                {form_fields && form_fields.length > 0 && (
                  <div className="flex flex-col gap-4">
                    {form_fields.map((field, index) =>
                      renderFormField(field, index),
                    )}
                  </div>
                )}

                {/* Error Message */}
                {errorMessage && (
                  <p className="text-red-400 text-sm text-center">
                    {errorMessage}
                  </p>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[rgba(234,234,234,0.3)] border-t border-[#eaeaea] rounded-full px-6 py-3 font-bold text-white text-center hover:bg-white/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting
                    ? 'Sending...'
                    : submit_button_label || 'Get Your Copy Now'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CtaWithSideForm;
